import { h } from 'preact';
import { useState, useEffect, useRef } from 'preact/hooks';
import { SessionList } from './SessionList';
import { EditableNote } from '../EditableNote';
import { CheckIcon, CopyIcon, EyeIcon, EyeOffIcon } from '../icons';
import { STORAGE_KEYS, getUserAlias, storageRemove } from '../../../constants.js';
import { copyWithFeedback } from '../../../shared.js';
import { updateUserKey, checkAliasAvailability, rotateUserKey, logout, createBillingPortalSession, hasProAccess, billingPlanName } from '../../services/api';
import { useBilling } from '../../contexts/BillingContext';
import { useAuth } from '../../contexts/AuthContext';
import { route } from '../HashRouter';

/**
 * Settings UI — shows authentication state and allows entering a new key.
 * With cookie-based auth, we don't store/display the key after initial setup.
 * Users can enter a different key to switch accounts.
 */
export function SettingsUI() {
  const [isRotatingKey, setIsRotatingKey] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [newKey, setNewKey] = useState<{ key: string; isRotated: boolean } | null>(null);
  const [showKeyRotationPrompt, setShowKeyRotationPrompt] = useState(false);
  const [currentKeyInput, setCurrentKeyInput] = useState('');
  const [rotationError, setRotationError] = useState<string | null>(null);
  const [showCurrentKey, setShowCurrentKey] = useState(false);
  const copyBtnRef = useRef<HTMLButtonElement>(null);
  const [fallbackAlias, setFallbackAlias] = useState<string | null>(null);
  const { subscription } = useBilling();
  const { user, loading: authLoading, refresh: refreshAuth } = useAuth();

  const isAuthenticated = user !== null;
  const userAlias = user ? user.userAlias : fallbackAlias;
  const isLoading = authLoading;

  useEffect(() => {
    if (!authLoading && !user) {
      getUserAlias().then(setFallbackAlias);
    }
  }, [authLoading, user]);

  const handleManageSubscription = async () => {
    try {
      const { uri } = await createBillingPortalSession();
      window.open(uri, '_blank');
    } catch (err) {
      console.error('[Vertex] Error opening billing portal:', err);
      alert('Failed to open billing portal. Please try again.');
    }
  };

  const handleLogout = async () => {
    if (!confirm('Are you sure you want to log out?')) {
      return;
    }
    setIsLoggingOut(true);
    try {
      await logout();
      // Clear local storage
      await storageRemove(STORAGE_KEYS.USER_ALIAS);
      // Redirect to onboarding
      window.location.href = '/onboarding';
    } catch (err) {
      console.error('[Vertex] Error logging out:', err);
      alert('Failed to log out. Please try again.');
    } finally {
      setIsLoggingOut(false);
    }
  };

  const handleCopy = async () => {
    if (copyBtnRef.current && newKey) {
      await copyWithFeedback(copyBtnRef.current, newKey.key);
    }
  };

  const handleRotateKeyClick = () => {
    setShowKeyRotationPrompt(true);
    setCurrentKeyInput('');
    setRotationError(null);
    setShowCurrentKey(false);
  };

  const handleCancelRotation = () => {
    setShowKeyRotationPrompt(false);
    setCurrentKeyInput('');
    setRotationError(null);
    setShowCurrentKey(false);
  };

  const handleConfirmRotation = async () => {
    if (!currentKeyInput.trim()) {
      setRotationError('Please enter your current key');
      return;
    }
    setIsRotatingKey(true);
    setRotationError(null);
    try {
      // rotateUserKey requires current key for Proof of Possession
      const { userKey } = await rotateUserKey(currentKeyInput.trim());
      setNewKey({ key: userKey, isRotated: true });
      setShowKeyRotationPrompt(false);
      setCurrentKeyInput('');
      await refreshAuth();
    } catch (err) {
      console.error('[Vertex] Error rotating key:', err);
      setRotationError('Invalid key or rotation failed. Please check your current key and try again.');
    } finally {
      setIsRotatingKey(false);
    }
  };

  const handleSaveAlias = async (newAlias: string | null) => {
    await updateUserKey(newAlias);
    await refreshAuth();
  };

  const validateAlias = async (alias: string): Promise<boolean> => {
    return checkAliasAvailability(alias);
  };

  if (isLoading) return <div class="screen"><div class="spinner">Loading...</div></div>;

  // Show new key (created or rotated) for user to save
  if (newKey) {
    return (
      <div class="screen">
        <div class="settings-auth-status">
          <span class="key-status-icon" style="color: #28a745;">
            <CheckIcon size={18} strokeWidth={2} />
          </span>
          <span>{newKey.isRotated ? 'Key rotated' : 'Account created'}</span>
        </div>
        <div class="created-key-display">
          <p class="created-key-label">Save this key somewhere safe:</p>
          <div class="created-key-row">
            <code class="created-key-value">{newKey.key}</code>
            <button ref={copyBtnRef} class="key-icon-btn" onClick={handleCopy} title="Copy key">
              <CopyIcon size={16} />
            </button>
          </div>
          <p class="created-key-warning">
            {newKey.isRotated
              ? 'Your old key no longer works. Save this new key to access your data.'
              : "You'll need this key to access your data from other devices."}
          </p>
        </div>
        <button class="btn-primary" onClick={() => setNewKey(null)}>
          Done
        </button>
      </div>
    );
  }

  return (
    <div class="screen">
      {isAuthenticated ? (
        <>
          <div class="settings-auth-status">
            <span class="key-status-icon" style="color: #28a745;">
              <CheckIcon size={18} strokeWidth={2} />
            </span>
            <span>Logged in</span>
          </div>
          <div class="alias-row">
            <span class="alias-label">Display name</span>
            <EditableNote
              value={userAlias}
              placeholder="Click to set"
              className="alias-value"
              onSave={handleSaveAlias}
              singleLine={true}
              onValidate={validateAlias}
            />
          </div>
          <div class="alias-row">
            <span class="alias-label">Plan</span>
            <span class="plan-value">
              {billingPlanName(subscription)}
            </span>
            {hasProAccess(subscription) && subscription !== 'whitelisted' && (
              <button class="btn-text" onClick={handleManageSubscription}>
                Manage
              </button>
            )}
          </div>
          {showKeyRotationPrompt ? (
            <div class="key-rotation-prompt">
              <p class="key-rotation-label">Enter your current key to rotate:</p>
              <div class="key-rotation-input-row">
                <input
                  type={showCurrentKey ? 'text' : 'password'}
                  class="key-rotation-input"
                  value={currentKeyInput}
                  onInput={(e) => setCurrentKeyInput((e.target as HTMLInputElement).value)}
                  placeholder="Your current key"
                  disabled={isRotatingKey}
                />
                <button
                  type="button"
                  class="key-visibility-toggle"
                  onClick={() => setShowCurrentKey(!showCurrentKey)}
                  title={showCurrentKey ? 'Hide key' : 'Show key'}
                >
                  {showCurrentKey ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                </button>
              </div>
              {rotationError && <p class="key-rotation-error">{rotationError}</p>}
              <div class="key-rotation-buttons">
                <button class="btn-text" onClick={handleCancelRotation} disabled={isRotatingKey}>
                  Cancel
                </button>
                <button class="btn-primary" onClick={handleConfirmRotation} disabled={isRotatingKey}>
                  {isRotatingKey ? 'Rotating...' : 'Rotate'}
                </button>
              </div>
            </div>
          ) : (
            <div class="settings-actions">
              <button class="btn-text" onClick={handleRotateKeyClick}>
                Rotate key
              </button>
              <button class="btn-text btn-danger" onClick={handleLogout} disabled={isLoggingOut}>
                {isLoggingOut ? 'Logging out...' : 'Logout'}
              </button>
            </div>
          )}
          <SessionList />
        </>
      ) : (
        <div class="settings-not-authenticated">
          <p>You are not logged in.</p>
          <button class="btn-primary" onClick={() => { window.location.href = '/onboarding'; }}>
            Log in or create account
          </button>
        </div>
      )}
    </div>
  );
}
